import { useEffect, useState } from 'react';
import { api, type NotaConGastos, type Proveedor, type Responsable } from '../api';
import { AvisoError } from '../componentes';

/**
 * Proveedor y responsable de la nota.
 *
 * Van juntos y plegados a propósito. El flujo normal —llegar a casa del
 * cliente, fotografiar el ticket, seguir— no los necesita: el responsable ya
 * viene puesto desde el empleado, y el proveedor solo hace falta cuando la
 * factura va a salir a nombre de alguien. Ponerlos desplegados metería dos
 * selectores por delante de lo único que importa con un ticket en la mano.
 *
 * Se abre solo si falta el proveedor y Gextia lo va a pedir, que es justo el
 * caso en el que conviene verlo sin buscarlo.
 */
export function DatosDeLaNota({
  nota,
  alCambiar,
}: {
  nota: NotaConGastos;
  alCambiar: () => void;
}) {
  const faltaProveedor = nota.vendorRequired && !nota.vendorId;
  const [abierto, setAbierto] = useState(faltaProveedor);
  const [responsables, setResponsables] = useState<Responsable[] | null>(null);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [busqueda, setBusqueda] = useState('');
  /** Mientras se escribe, el campo enseña la búsqueda; si no, el proveedor puesto. */
  const [escribiendo, setEscribiendo] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  async function elegir(proveedorId: number | null) {
    setEscribiendo(false);
    setBusqueda('');
    await guardar({ proveedorId });
  }

  // Los responsables se piden una vez al desplegar: son pocos y no cambian
  // durante la sesión. Los proveedores no — pueden ser miles, así que se
  // buscan escribiendo.
  useEffect(() => {
    if (!abierto || responsables) return;
    api.responsables().then(setResponsables).catch(setError);
  }, [abierto, responsables]);

  useEffect(() => {
    if (!abierto) return;
    // Pequeña espera para no lanzar una petición por tecla pulsada.
    const temporizador = setTimeout(() => {
      api.proveedores(busqueda).then(setProveedores).catch(setError);
    }, 250);
    return () => clearTimeout(temporizador);
  }, [abierto, busqueda]);

  async function guardar(cambios: {
    proveedorId?: number | null;
    responsableId?: number | null;
  }) {
    setGuardando(true);
    setError(null);
    try {
      await api.actualizarNota(nota.id, cambios);
      alCambiar();
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  if (!abierto) {
    return (
      <button type="button" className="desplegable" onClick={() => setAbierto(true)}>
        <span className="tarjeta-meta">
          {nota.vendorName || 'Sin proveedor'}
          {nota.approverName ? ` · aprueba ${nota.approverName}` : ''}
        </span>
        <span aria-hidden="true">▾</span>
      </button>
    );
  }

  return (
    <div className="tarjeta bloque-datos">
      <AvisoError error={error} />

      {/*
        Un solo campo: se escribe encima del proveedor puesto y las coincidencias
        salen debajo. Antes eran dos controles —un buscador y un desplegable—
        para elegir la misma cosa, que obliga a entender la relación entre ellos
        antes de poder usarlos.
      */}
      <div className="campo">
        <label htmlFor="proveedor">Proveedor</label>
        <input
          id="proveedor"
          type="search"
          value={escribiendo ? busqueda : nota.vendorName}
          onChange={(e) => {
            setEscribiendo(true);
            setBusqueda(e.target.value);
          }}
          onFocus={() => {
            setEscribiendo(true);
            setBusqueda('');
          }}
          placeholder="Sin proveedor"
          autoComplete="off"
          enterKeyHint="search"
          disabled={guardando}
        />

        {escribiendo && (
          <ul className="sugerencias">
            {/* Quitarlo es una opción más de la lista, no un botón aparte. */}
            {nota.vendorId !== null && (
              <li>
                <button type="button" onClick={() => void elegir(null)}>
                  Sin proveedor
                </button>
              </li>
            )}
            {proveedores.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={p.id === nota.vendorId ? 'elegido' : undefined}
                  onClick={() => void elegir(p.id)}
                >
                  {p.name}
                </button>
              </li>
            ))}
            {proveedores.length === 0 && (
              <li className="tarjeta-meta">Ningún proveedor con ese nombre.</li>
            )}
          </ul>
        )}
      </div>

      {faltaProveedor && (
        <p className="aviso atencion">
          Esta nota va a necesitar proveedor cuando se contabilice. Puedes
          enviarla sin él, pero alguien tendrá que ponerlo después.
        </p>
      )}

      <div className="campo">
        <label htmlFor="responsable">Quién la aprueba</label>
        <select
          id="responsable"
          value={nota.approverId ?? ''}
          disabled={guardando || !responsables}
          onChange={(e) =>
            void guardar({ responsableId: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">{responsables ? 'Sin asignar' : 'Cargando…'}</option>
          {/* El que ya viene puesto puede no estar en el grupo de aprobadores
              (Gextia lo hereda del empleado). Sin esta opción, el selector se
              vería vacío y parecería que se ha perdido. */}
          {nota.approverId !== null &&
            !(responsables ?? []).some((r) => r.id === nota.approverId) && (
              <option value={nota.approverId}>{nota.approverName}</option>
            )}
          {(responsables ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        {responsables?.length === 0 && (
          <span className="ayuda">
            No se ha podido cargar la lista de aprobadores. Se queda el que trae
            la nota.
          </span>
        )}
      </div>

      <button type="button" className="boton texto" onClick={() => setAbierto(false)}>
        Listo
      </button>
    </div>
  );
}
